import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train } from 'lucide-react';
import CTA from '../../components/CTA';

const AntrimHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Antrim | Serving Antrim from Belfast Clinic</title>
        <meta
          name="description"
          content="Professional hypnotherapy serving Antrim from our Belfast clinic (18 miles). Work-life balance, commuter stress, anxiety, retail worker support. CNHC registered."
        />
        <meta name="keywords" content="hypnotherapy Antrim, hypnotherapist Antrim, anxiety treatment Antrim, commuter stress, work-life balance, retail worker anxiety Steeple Rathenraw" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/antrim-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Antrim | Serving Antrim from Belfast Clinic" />
        <meta property="og:description" content="Professional hypnotherapy serving Antrim from our Belfast clinic. Work-life balance, commuter stress, anxiety support." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Antrim",
            "description": "CNHC registered hypnotherapy serving Antrim from our Belfast clinic. Specializing in work-life balance, commuter stress, anxiety management, retail worker support, and parenting stress for busy families.",
            "url": "https://www.sharonclare-hypnotherapy.com/antrim-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Antrim",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Antrim"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Work-Life Balance Support",
              "Commuter Stress Management",
              "Retail Worker Anxiety",
              "Parenting Stress for Dual-Income Families",
              "Smoking Cessation"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy Serving Antrim
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Based in Belfast, serving Antrim and surrounding market town areas
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">18 miles</span> from Antrim via M2 • <span className="font-semibold">25-30 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Regular train service into Belfast • Online sessions for maximum flexibility</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Antrim and the Market Town Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                While I don't have a physical clinic in Antrim, I'm delighted to serve this vibrant market town community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast. For Antrim residents, accessing professional hypnotherapy is straightforward and convenient - at just 18 miles south via the M2 motorway, the journey takes only 25-30 minutes by car. This is a manageable distance for regular weekly sessions, especially since many Antrim residents already make this commute daily for work in Belfast.
              </p>
              <p>
                Antrim is a town of approximately 24,000 people, characterized by its dual identity as both a traditional market town and a modern commuter hub. The town's proximity to Belfast International Airport and the M2 corridor has transformed it into a shopping destination - Junction One outlet center, Castle Mall, and numerous retail developments attract visitors from across County Antrim. This creates a unique demographic profile: busy families juggling Belfast employment with Antrim home life, retail and service workers managing customer-facing pressures, and a growing community seeking the balance of suburban affordability with urban accessibility.
              </p>
              <p>
                Whether you're a Belfast commuter struggling with work-life balance, a retail professional managing customer service stress at Junction One or Castle Mall, a parent coordinating school runs around dual work schedules, or anyone in Antrim dealing with anxiety, poor sleep, unwanted habits, or other challenges, hypnotherapy offers evidence-based support tailored to the realities of modern market town living. The M2 route you already know becomes a pathway to professional mental health support that fits your busy schedule.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Antrim Residents Choose Belfast Clinic */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Why Antrim Residents Choose Our Belfast Clinic
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Car className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Familiar Commute Route
                    </h3>
                    <p className="text-gray-700">
                      Many Antrim residents already travel the M2 to Belfast for work daily. Adding a therapy session before or after work is straightforward - no new route to learn, just 25-30 minutes on a motorway you know well.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Manageable Distance for Regular Sessions
                    </h3>
                    <p className="text-gray-700">
                      At 18 miles, Belfast is close enough for consistent weekly therapy appointments. This isn't a long-distance journey requiring significant planning - it's a reasonable drive for the professional support you need.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Clock className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Flexible Scheduling for Busy Lives
                    </h3>
                    <p className="text-gray-700">
                      Morning, afternoon, and evening appointments available to fit around work schedules, school runs, and retail shifts. Book sessions that align with your Belfast commute or choose quieter times to avoid rush hour.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <MapPin className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Online Alternative When Needed
                    </h3>
                    <p className="text-gray-700">
                      Can't make the drive on a particular week? School run clash? Online video sessions provide the same quality therapy without leaving Antrim - maximum flexibility for unpredictable schedules.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Antrim Community */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Services Tailored to Antrim's Market Town Community
            </h2>
            <p className="text-lg text-gray-700 mb-12">
              Living in Antrim brings unique stressors - the push and pull of commuter life, retail sector pressures, juggling family responsibilities with Belfast employment, and the challenge of maintaining work-life balance when home and work are 18 miles apart. Hypnotherapy addresses these modern market town realities with evidence-based techniques.
            </p>

            <div className="space-y-10">
              {/* Work-Life Balance for Commuters */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Work-Life Balance and Commuter Stress Management
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    For the thousands of Antrim residents who commute to Belfast for work, the daily 50-60 minute round trip adds up to significant time away from home - around 250 hours per year just in transit. This creates a unique form of stress: guilt about missing school events, exhaustion from early starts and late returns, anxiety about M2 traffic delays, and the feeling that there's never enough time for self-care or family connection.
                  </p>
                  <p>
                    Hypnotherapy helps commuters reclaim mental space and establish boundaries. Techniques include: reframing the commute as "transition time" rather than lost time, reducing anticipatory anxiety about traffic, establishing evening routines that prioritize family connection over work rumination, managing guilt about work-home balance, and building resilience against the exhaustion of dual-location living.
                  </p>
                  <p>
                    Many Antrim clients report that hypnotherapy sessions scheduled before or after work in Belfast become part of a healthier routine - instead of rushing home to collapse, they invest 60 minutes in mental health support that improves their entire week. The commute becomes an opportunity rather than just an obligation.
                  </p>
                </div>
              </div>

              {/* Retail and Service Worker Anxiety */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Retail and Service Worker Anxiety and Stress Support
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Junction One outlet center, Castle Mall, and Antrim's growing retail sector employ hundreds of local residents in customer-facing roles. While these jobs provide convenient local employment without the Belfast commute, they come with distinct pressures: demanding customers, sales targets, standing all day, weekend and evening shifts that disrupt family time, seasonal spikes (Christmas rush), and the emotional labor of maintaining a friendly demeanor regardless of how you're actually feeling.
                  </p>
                  <p>
                    Retail workers often experience anticipatory anxiety before shifts (especially busy weekends), exhaustion from emotional suppression ("the customer is always right"), sleep disruption from irregular schedules, and frustration at feeling undervalued despite working hard in essential roles. Physical symptoms like tension headaches, jaw clenching, and digestive issues are common.
                  </p>
                  <p>
                    Hypnotherapy provides practical tools for retail professionals: techniques to reduce pre-shift anxiety, strategies to create emotional boundaries with difficult customers (compassion without absorption), methods to recover quickly from stressful interactions, sleep optimization for shift workers, and confidence-building to counteract the feeling of being "just a shop assistant." You deserve mental health support for the real pressures you face every day.
                  </p>
                </div>
              </div>

              {/* Parenting Stress for Dual-Income Families */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Parenting Stress for Dual-Income Families
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Antrim's commuter demographic often includes dual-income families where both parents work in Belfast or locally. The logistics are complex: coordinating school drop-offs and pick-ups, juggling after-school activities, managing homework while preparing dinner after a long commute, and finding time for meaningful family connection beyond the evening scramble. Many parents feel they're constantly "managing" rather than truly parenting.
                  </p>
                  <p>
                    Common stressors include: guilt about leaving children with grandparents or childcare while working, anxiety about missing school events or milestones, frustration at having no time for self-care, worry about children's wellbeing while feeling too stretched to address concerns properly, and the constant mental load of coordinating family logistics. The pressure to "have it all" while feeling you're failing at everything.
                  </p>
                  <p>
                    Hypnotherapy helps parents develop realistic expectations, release guilt that serves no purpose, establish boundaries around work demands (saying no without shame), improve sleep quality despite racing thoughts, and reconnect with the reasons you chose parenthood beyond the logistics treadmill. Sessions can be scheduled around school hours, during Belfast work hours, or online from home when childcare is challenging.
                  </p>
                </div>
              </div>

              {/* Anxiety and Stress Management */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  General Anxiety and Stress Management
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Beyond specific roles, many Antrim residents experience generalized anxiety - persistent worry, racing thoughts, physical tension, sleep disturbances, and the feeling of being overwhelmed by modern life. Living in a growing commuter town brings particular pressures: financial stress from mortgages and commuting costs, social comparison through social media, busy schedules that leave no downtime, and the pace of change in what was once a quieter market town.
                  </p>
                  <p>
                    Hypnotherapy addresses anxiety at the subconscious level where automatic worry patterns are stored. Clinical hypnosis helps retrain the nervous system's threat response, reducing the "background hum" of constant tension. Techniques include: progressive relaxation to release physical holding patterns, cognitive reframing to challenge catastrophic thinking, visualization to create a felt sense of safety, and anchoring techniques to access calm states quickly during stressful moments.
                  </p>
                  <p>
                    Research shows hypnotherapy can be as effective as CBT for anxiety disorders, with some clients reporting significant improvement in just 4-6 sessions. The approach is particularly helpful for those who find traditional talk therapy too slow or intellectual - hypnotherapy works directly with the emotional brain where anxiety originates.
                  </p>
                </div>
              </div>

              {/* Additional Services */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Sleep Problems and Insomnia
                  </h4>
                  <p className="text-gray-700">
                    Racing thoughts keeping you awake? Early morning anxiety waking you at 4am? Hypnotherapy helps quiet the mental chatter and retrain your nervous system for restful sleep - essential for managing busy Antrim schedules.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Smoking Cessation and Habit Change
                  </h4>
                  <p className="text-gray-700">
                    Ready to quit smoking? Break stress-eating patterns? Address nail-biting or other unwanted habits? Hypnotherapy targets the subconscious drivers of automatic behaviors - more effective than willpower alone.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Chronic Pain Management
                  </h4>
                  <p className="text-gray-700">
                    Living with fibromyalgia, back pain, migraines, or other persistent pain? Hypnotherapy helps reduce pain intensity and improve quality of life by working with the nervous system's pain processing pathways. NHS-complementary support.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Confidence and Self-Esteem
                  </h4>
                  <p className="text-gray-700">
                    Struggling with imposter syndrome at work? Social anxiety in Antrim's close-knit community? Hypnotherapy helps build genuine confidence from within - not fake-it-till-you-make-it, but authentic self-acceptance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Getting to the Belfast Clinic from Antrim
            </h2>

            <div className="bg-white rounded-xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <Car className="w-6 h-6 text-accent-600 mr-3" />
                By Car (Recommended)
              </h3>
              <div className="space-y-3 text-gray-700">
                <p className="font-semibold">Distance: approximately 18 miles | Drive time: around 30 minutes off-peak, longer at rush hour</p>
                <p><strong>Route:</strong></p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>From Antrim town center, head south on A6 toward M2 junction</li>
                  <li>Join M2 motorway southbound toward Belfast</li>
                  <li>Follow the M2 all the way into Belfast</li>
                  <li>Continue through the city and head south towards the Ormeau Road (A24)</li>
                  <li>The clinic is at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ - roughly 10 minutes from the city centre</li>
                </ol>
                <p className="text-sm italic">Tip: Many Antrim commuters already know the M2 run from daily Belfast work travel - the clinic sits just south of the city centre on the Ormeau Road, so it is an easy continuation of a familiar journey.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <Train className="w-6 h-6 text-accent-600 mr-3" />
                By Train
              </h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>Route:</strong> Antrim Station to Belfast (Translink service)</p>
                <p><strong>Frequency:</strong> Regular service throughout the day (check Translink timetable)</p>
                <p><strong>Journey time:</strong> Approximately 25-30 minutes into Belfast</p>
                <p><strong>From central Belfast to the clinic:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Botanic and City Hospital are the closest stations to the clinic, with Lanyon Place serving regional routes</li>
                  <li>Metro bus services run along the Ormeau Road corridor into south Belfast</li>
                  <li>OR take a taxi - the clinic is around 10 minutes from the city centre</li>
                </ul>
                <p className="text-sm italic">Tip: Train is ideal if you prefer not to drive in Belfast traffic or want to use travel time for reflection.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <MapPin className="w-6 h-6 text-accent-600 mr-3" />
                Online Sessions
              </h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  Can't make the journey on a particular week? Prefer the convenience of therapy from home? <strong>Online video sessions</strong> provide the same professional support without the 18-mile round trip.
                </p>
                <p>
                  Online hypnotherapy is particularly helpful for: busy parents juggling childcare, retail workers with irregular shift patterns, anyone managing the session around work commitments, or during poor weather when M2 travel is challenging.
                </p>
                <p>
                  Sessions conducted via secure video platform - just as effective as in-person for most clients, with maximum flexibility for your Antrim schedule.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Questions from Antrim Clients
            </h2>
            <div className="space-y-6">

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is Antrim close enough for regular weekly sessions?
                </h3>
                <p className="text-gray-700">
                  Yes - at 18 miles and 25-30 minutes via the M2, this is a manageable distance for consistent therapy. Many Antrim residents already commute to Belfast daily for work, so the route is familiar. You can schedule sessions before or after work, during lunch breaks, or at quieter times to avoid rush hour. Online sessions are also available if you prefer flexibility on particular weeks.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you work with Belfast commuters specifically?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Work-life balance and commuter stress are common issues I address with Antrim clients. Many find that building therapy sessions into their Belfast routine (before or after work) helps establish healthier boundaries and creates dedicated time for mental health support. We work on reframing commute time, reducing anticipatory anxiety about traffic, managing evening exhaustion, and establishing routines that prioritize family connection.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can I fit appointments around school runs and work?
                </h3>
                <p className="text-gray-700">
                  Yes - I offer flexible scheduling including morning appointments (after school drop-off), mid-morning/afternoon (during work hours if you can take time), and evening slots (after pick-up). For parents juggling dual work schedules, online sessions can be particularly helpful - no travel time means you can fit a session during lunch, between meetings, or after children are in bed.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can you help with retail worker stress from Junction One or Castle Mall?
                </h3>
                <p className="text-gray-700">
                  Yes. Retail and service workers face real pressures - demanding customers, sales targets, emotional labor, irregular shifts, and weekend work. Hypnotherapy helps you develop emotional boundaries with difficult customers, reduce pre-shift anxiety, recover quickly from stressful interactions, and build confidence in your professional value. Your mental health matters, regardless of your job title.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What if I already travel to Belfast for work - can I add a session?
                </h3>
                <p className="text-gray-700">
                  This is ideal. Many Antrim commuters schedule therapy before work (early morning appointment, then continue to your Belfast office) or after work (session at 5pm or 6pm before heading home). This makes efficient use of your commute day and means you're investing in mental health as part of your routine rather than adding an extra journey. Evening appointments help you process work stress before returning home to family.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  This varies by individual and concern. Many clients notice improvement within 4-6 sessions. Some issues (specific phobias, smoking cessation) may respond in 1-3 sessions. Deeper patterns (chronic anxiety, long-term stress) may benefit from 8-12 sessions. We'll discuss your goals during the free initial consultation and create a realistic plan. You're in control of the pace.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy covered by health insurance?
                </h3>
                <p className="text-gray-700">
                  Some private health insurance plans cover hypnotherapy if provided by a CNHC-registered practitioner (I am). Check your policy details or contact your insurer. If not covered, package discounts are available when you book multiple sessions. Many clients find the investment worthwhile compared to ongoing costs of stress, poor sleep, or unwanted habits.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What's the difference between online and in-person sessions?
                </h3>
                <p className="text-gray-700">
                  The therapy is equally effective - hypnosis works through voice, guidance, and your own subconscious processes, not physical presence. Online sessions offer convenience (no 18-mile round trip), flexibility (easier to fit around work/family), and comfort (therapy from your own space). In-person sessions provide dedicated time away from home distractions and face-to-face connection. Many clients use a hybrid approach based on their schedule each week.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are you qualified? How do I verify CNHC registration?
                </h3>
                <p className="text-gray-700">
                  Yes - I'm registered with the CNHC (Complementary and Natural Healthcare Council), the UK regulatory body for hypnotherapists accredited by the Professional Standards Authority. This requires 450+ hours of training, professional membership, insurance, and adherence to strict ethical standards. You can verify my registration at www.cnhc.org.uk by searching for my name. I also have 15 years of nursing experience at Belfast City Hospital, bringing a healthcare professional's understanding to therapy.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What happens in a hypnotherapy session?
                </h3>
                <p className="text-gray-700">
                  Sessions begin with discussion about your concerns and goals (what brings you, what you want to change). Then guided relaxation - I help you into a calm, focused state (you remain fully aware and in control). In this relaxed state, we work with your subconscious mind using visualization, suggestion, and reframing techniques specific to your issue. You emerge feeling calm and refreshed. Each session is tailored to your needs, not a generic script.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              What Clients from County Antrim Say
            </h2>
            <div className="grid md:grid-cols-2 gap-8">

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I was skeptical about driving to Belfast for therapy, but Sharon made it so easy to schedule around my work commute. Now I book sessions at 5pm on my way home from the office - it's become part of my routine and helps me decompress before getting back to family responsibilities. The work-life balance techniques have been transformative."
                </p>
                <p className="text-gray-900 font-semibold">— L.M., Antrim Town</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "As a retail worker at Junction One, the customer service stress was affecting my sleep and home life. Sharon understood the specific pressures of retail work and gave me practical tools to set emotional boundaries. I can handle difficult customers now without taking it home with me. The online sessions were perfect for fitting around my shift patterns."
                </p>
                <p className="text-gray-900 font-semibold">— K.T., Steeple</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "With two young children and both of us working in Belfast, the parenting guilt was overwhelming. Sharon helped me release the unrealistic expectations and establish better boundaries at work. The anxiety about 'failing' at everything has significantly reduced. I wish I'd started therapy years ago - it's not self-indulgent, it's essential."
                </p>
                <p className="text-gray-900 font-semibold">— A.D., Rathenraw</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I quit smoking after 15 years with Sharon's help - only took 3 sessions. The hypnotherapy approach worked where willpower and patches had failed. The drive from Antrim was easy, and the money I'm saving on cigarettes has already paid for the sessions ten times over. Highly recommend to anyone in Antrim struggling with habits."
                </p>
                <p className="text-gray-900 font-semibold">— J.R., Greystone</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The generalized anxiety I'd lived with for years has reduced dramatically after 8 sessions. Sharon's approach is professional but warm - she understands the pressures of modern life in busy Antrim. The Belfast clinic is comfortable and easy to reach. I've recommended her to several friends already."
                </p>
                <p className="text-gray-900 font-semibold">— S.H., Fountain Hill</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />

    </main>
  );
};

export default AntrimHypnotherapy;
